import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiDelete, apiPatch, apiPost, apiPut } from "../utils/api";
import { showError, showSuccess } from "../utils/alert";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { parseError } from "../utils/errors";

type Method = "PUT" | "PATCH" | "POST" | "DELETE";

export function useUpdateResource() {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const updateResource = async (
    url: string,
    data: any,
    optionsOrTokenType?: string | {
      method?: Method;
      invalidateQueries?: string[];
      successMessage?: string;
      navigateAfter?: () => void;
      confirmMessage?: string;
      showNoteInput?: boolean;
    },
    legacyOptions?: {
      method?: Method;
      invalidateQueries?: string[];
      successMessage?: string;
      navigateAfter?: () => void;
      confirmMessage?: string;
      showNoteInput?: boolean;
    }
  ) => {
    const options = typeof optionsOrTokenType === 'string'
      ? legacyOptions
      : optionsOrTokenType;
    setLoading(true);
    try {
      let note = "";

      if (options?.confirmMessage) {
        const result = await Swal.fire({
          title: options.confirmMessage,
          icon: "question",
          input: options?.showNoteInput ? "textarea" : undefined,
          inputPlaceholder: t("optional_cancel_note") || "Reason (optional)",
          inputAttributes: {
            "aria-label": "Type your reason here",
          },
          showCancelButton: true,
          confirmButtonText: t("yes"),
          cancelButtonText: t("cancel"),
          customClass: {
            confirmButton:
              "bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700",
            cancelButton:
              "bg-gray-200 text-black px-4 py-2 rounded hover:bg-gray-300",
          },
          preConfirm: (value) => {
            if (options?.showNoteInput) {
              note = value || "";
            }
          },
        });

        if (!result.isConfirmed) {
          setLoading(false);
          return false;
        }
      }

      const payload = (() => {
        if (options?.showNoteInput && note) {
          if (data instanceof FormData) {
            data.append('note', note);
            return data;
          }
          return { ...data, note };
        }
        return data;
      })();

      if (options?.method === "PATCH")
        await apiPatch(url, payload);
      else if (options?.method === "PUT")
        await apiPut(url, payload);
      else if (options?.method === "DELETE")
        await apiDelete(url);
      else await apiPost(url, payload);

      if (options?.invalidateQueries) {
        options.invalidateQueries.forEach((key) =>
          queryClient.invalidateQueries({ queryKey: [key], exact: false })
        );
      }

      if (options?.successMessage) showSuccess(options.successMessage);
      if (options?.navigateAfter) options.navigateAfter();
      return true;
    } catch (err: any) {
      const { message } = parseError(err);
      showError(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { loading, updateResource };
}
